/**
 * The JetBrains Air release design as CoverBaker data.
 *
 * The design lives in one Figma frame, `CoverBaker / IJ release / mapped
 * templates + DB backgrounds` (29:526) on the `Release: JetBrains Air` page of
 * the CoverBaker Templates file (fOPjc0qhtJ5g9mEiiQNyav). Every template
 * there is a row of `<Template> / background-NN` frames, one per artwork, and
 * each artwork layer carries shared plugin data (`coverbaker.ij` / `background`)
 * with the layer's CSS box and matrix. `scripts/air-design-from-figma.mjs`
 * reads those frames and writes `src/data/air-design.json`; this module turns
 * the manifest into the crops, opacities and visibility a scene stores.
 *
 * Facts come from the manifest. Policy — which CoverBaker template reads which
 * Figma frame, which templates the design does not draw — is stated here, so a
 * manifest refresh cannot change it silently.
 *
 * Dependency-free apart from the manifest and the template sizes: `src/libs`
 * ships with the server image next to `src/data`.
 */

import manifest from "../data/air-design.json";
// Canvas sizes, to re-project an analog's crop. templateManifest is React-free
// data, the same module the renderer reads sizes from, so `src/libs` keeps
// shipping next to `src/data` with nothing else behind it.
import {getTemplateSize} from "../data/templateManifest";

export interface AirCrop {
  /** Scaled, unrotated image's left edge inside the template, in template px. */
  x: number;
  /** Scaled, unrotated image's top edge inside the template, in template px. */
  y: number;
  /** Multiplier on the asset's intrinsic size. */
  scale: number;
  /** Clockwise, in degrees. The renderer turns the image about its centre. */
  rotate: 0 | 90 | 180 | 270;
  /** Mirror about the image's centre, applied after the rotation. */
  flip: {x: boolean; y: boolean};
}

/** One artwork layer as the Figma frame draws it. */
export interface FigmaLayerGeometry {
  /** The layer's own (unrotated) box, in frame px. */
  width: number;
  height: number;
  /** The CSS `matrix(a, b, c, d, e, f)` the plugin data stores, origin 0 0. */
  matrix: [number, number, number, number, number, number];
  /** The intrinsic size of the image the layer is filled with. */
  imageWidth: number;
  imageHeight: number;
}

export interface AirBackgroundAsset {
  /** Position in the Air background list, 1-based, in the frame's order. */
  position: number;
  /** The `db-*` source name the frames use for this image. */
  source: string;
  /** Figma's image hash, the key into the file's image map. */
  imageRef: string;
  width: number;
  height: number;
}

/** A crop plus the artwork's fill opacity, in the percent a scene stores. */
export interface AirPlacement extends AirCrop {
  opacity: number;
}

export interface AirDesignManifest {
  source: {file: string; node: string; page: string};
  /** The frame fill every design sits on, as the catalog stores a base. */
  base: string;
  backgrounds: AirBackgroundAsset[];
  /** `placements[templateId][position]`: the artwork on the plain layouts. */
  placements: Record<string, Record<string, AirPlacement>>;
  /**
   * `layoutPlacements[layout][templateId][position]`: the artwork on a layout
   * that moves it — the screenshot layout shifts the person left of the window.
   */
  layoutPlacements: Record<string, Record<string, Record<string, AirPlacement>>>;
}

export const AIR_DESIGN = manifest as AirDesignManifest;

/** The frame fill every Air design composites over, as the catalog stores a base. */
export const AIR_BASE_COLOR = AIR_DESIGN.base;

/**
 * Style markers the Air backgrounds carry. The design is light, so the
 * JetBrains logo has to resolve to its black variant; `blacktext` is
 * deliberately absent — it forces pure #000 over every text node, and the
 * frame's text is #19191C.
 */
export const AIR_BACKGROUND_MARKERS = ["blacklogo"] as const;

/**
 * Templates the frame does not draw but that share a canvas with one it does.
 * Email04 is the Email03 strip (1300x650) under another name.
 */
const AIR_TEMPLATE_ALIASES: Record<string, string> = {
  Email04: "Email03",
};

/**
 * Templates in Air's sets that the design does not include. The frame's README
 * lists the exact members of every set; these are the CoverBaker templates of
 * those sets that it leaves out. The default scenes hide them through the
 * set's `visible` list, the same field the Defaults panel's Visibility group
 * writes, so an admin can bring one back from the editor.
 */
export const AIR_HIDDEN_TEMPLATES: Record<string, string[]> = {
  // Horizontal Features and the square Email01 are published banners, so they
  // stay on the page and take their artwork from an analog (below). Instagram
  // Feed is the one Social canvas Air does not publish, and Email04 is the
  // Email03 strip under another name — a duplicate, not a second banner.
  "social-media": ["InstagramFeed"],
  emails: ["Email04"],
};

/**
 * The templates a layout is drawn for, when the design does not draw it on all
 * of them.
 *
 * Layout 5 puts the headline beside an uploaded screenshot. The frame draws
 * that pairing on three canvases only (`layoutPlacements` in the manifest has
 * the same three), so the set shows those banners alone while the layout is
 * on: the rest have no Air frame with a screenshot window. A layout absent
 * from this record is drawn for every template of its set.
 */
export const AIR_LAYOUT_TEMPLATES: Record<string, readonly string[]> = {
  5: ["Twitter", "BlogFeatured", "BlogSocialShare"],
};

/** The templates a layout is drawn for, or null when it is drawn for all. */
export function airLayoutTemplateIds(layout: number | string): readonly string[] | null {
  return AIR_LAYOUT_TEMPLATES[String(layout)] ?? null;
}

/**
 * The template whose crop stands in for one the frame does not draw.
 *
 * The frame draws ten canvases; Air's sets hold three more. A centred cover
 * fit puts the artwork wherever the canvas happens to crop it, which reads as
 * a different design — so each of them names the DRAWN canvas closest in
 * aspect and orientation, and `reprojectPlacement` carries that crop over.
 * Same shape, same reading; the analog's fill opacity comes with it, because
 * the frame fades the artwork per canvas and 80 vs 100 is part of the look.
 *
 * Horizontal Features is 1200x675, Twitter's canvas exactly, so it is a copy.
 * Email01 is the only square in the email set and takes the square social
 * banner. Instagram Feed is 1080x1440 against SoMe Vertical's 1080x1350 — the
 * nearest portrait, and the same width.
 */
const AIR_TEMPLATE_ANALOGS: Record<string, string> = {
  HorizontalFeatures: "Twitter",
  Email01: "YouTubeCommunity",
  InstagramFeed: "SoMeVertical",
};

/**
 * One canvas's crop on another: the whole placement scaled about the canvas's
 * top-left corner, by the ratio of the widths.
 *
 * The artwork is not a full-bleed background — it is a collage element on the
 * #EDEBE4 base, hung off the TOP of the canvas with the headline and the logo
 * under it, and it usually reaches past the left and right edges. So the
 * width sets its size, and the top-left corner is the fixed point: a taller
 * canvas gets the same artwork in the same place and more room beneath it,
 * which is where that canvas's extra text goes.
 *
 * Two canvases of the same shape scale uniformly, so the crop keeps its
 * framing exactly; identical canvases give k=1 and the analog's crop
 * unchanged, to the bit.
 */
function reprojectPlacement(
  placement: AirPlacement,
  from: {width: number},
  to: {width: number}
): AirPlacement {
  const k = to.width / from.width;
  if (k === 1) return {...placement, flip: {...placement.flip}};
  return {
    ...placement,
    x: placement.x * k,
    y: placement.y * k,
    scale: placement.scale * k,
    flip: {...placement.flip},
  };
}

/** Does this canvas shape stack the text over the logo at the bottom? */
export function isAirStackedCanvas(templateWidth: number, templateHeight: number): boolean {
  return templateWidth / templateHeight < 1.4;
}

const ROTATIONS: Array<{key: string; rotate: AirCrop["rotate"]; flip: AirCrop["flip"]}> = [
  {key: "1,0,0,1", rotate: 0, flip: {x: false, y: false}},
  {key: "-1,0,0,1", rotate: 0, flip: {x: true, y: false}},
  {key: "1,0,0,-1", rotate: 0, flip: {x: false, y: true}},
  {key: "-1,0,0,-1", rotate: 180, flip: {x: false, y: false}},
  {key: "0,1,-1,0", rotate: 90, flip: {x: false, y: false}},
  {key: "0,-1,1,0", rotate: 270, flip: {x: false, y: false}},
];

/**
 * The scene crop that draws a Figma artwork layer.
 *
 * The frame fills the layer box with the image (Figma FILL: cover the box,
 * centred) and then applies the layer's matrix with the origin at the box's
 * top-left. CoverBaker draws the image at its natural size, turns and mirrors
 * it about its own centre, then scales and translates it (BiggerPicture). Both
 * keep the centre of the box and the centre of the image together, so the
 * crop is: the FILL scale, the matrix's rotation, and the translation that
 * puts the unrotated image's centre where the matrix puts the box's centre.
 *
 * Only quarter turns and axis mirrors are accepted: that is what the frames
 * use and what the renderer's controls offer.
 */
export function figmaLayerToCrop({
  width,
  height,
  matrix,
  imageWidth,
  imageHeight,
}: FigmaLayerGeometry): AirCrop {
  const [a, b, c, d, e, f] = matrix;
  const key = [a, b, c, d].map((v) => String(Math.round(v))).join(",");
  const turn = ROTATIONS.find((r) => r.key === key);
  if (!turn) {
    throw new Error(`Layer matrix [${a}, ${b}, ${c}, ${d}] is not a quarter turn or a mirror`);
  }
  if (!(width > 0 && height > 0 && imageWidth > 0 && imageHeight > 0)) {
    throw new Error("Layer and image sizes must be positive");
  }

  const scale = Math.max(width / imageWidth, height / imageHeight);
  // The box's centre after the matrix.
  const cx = a * (width / 2) + c * (height / 2) + e;
  const cy = b * (width / 2) + d * (height / 2) + f;
  return {
    x: cx - (imageWidth * scale) / 2,
    y: cy - (imageHeight * scale) / 2,
    scale,
    rotate: turn.rotate,
    flip: {...turn.flip},
  };
}

/** The design's template for a CoverBaker template id, through the aliases. */
export function airDesignTemplateId(templateId: string): string {
  return AIR_TEMPLATE_ALIASES[templateId] ?? templateId;
}

/** The background at a list position, or a clear error. */
export function airBackground(position: number | string): AirBackgroundAsset {
  const asset = AIR_DESIGN.backgrounds.find((b) => String(b.position) === String(position));
  if (!asset) {
    throw new Error(
      `Air has no background at position ${position} — the design has ${AIR_DESIGN.backgrounds.length}`
    );
  }
  return asset;
}

/**
 * Where one template puts one background, on one layout.
 *
 * `layout` names a CoverBaker layout with its own artwork placement in the
 * frames (the screenshot layout); null is the plain arrangement. A layout
 * without a placement of its own for the template falls back to the plain
 * one, so every layout has an answer.
 *
 * A template the frame does not draw takes the crop of its analog
 * (AIR_TEMPLATE_ANALOGS), re-projected onto its own canvas. Only a template
 * with neither a frame nor an analog falls back to a centred cover fit.
 */
export function airPlacement(
  templateId: string,
  templateWidth: number,
  templateHeight: number,
  position: number | string,
  layout: number | string | null = null
): AirPlacement {
  const asset = airBackground(position);
  const designId = airDesignTemplateId(templateId);
  const key = String(asset.position);

  if (layout !== null) {
    const onLayout = AIR_DESIGN.layoutPlacements[String(layout)]?.[designId]?.[key];
    if (onLayout) return onLayout;
  }
  const plain = AIR_DESIGN.placements[designId]?.[key];
  if (plain) return plain;

  const analogId = AIR_TEMPLATE_ANALOGS[designId];
  if (analogId) {
    // The analog is a drawn canvas, so this recursion ends on its own frame.
    const {width, height} = getTemplateSize("releases", analogId);
    const analog = airPlacement(analogId, width, height, position, layout);
    return reprojectPlacement(analog, {width}, {width: templateWidth});
  }

  const scale = Math.max(templateWidth / asset.width, templateHeight / asset.height);
  return {
    x: (templateWidth - asset.width * scale) / 2,
    y: (templateHeight - asset.height * scale) / 2,
    scale,
    rotate: 0,
    flip: {x: false, y: false},
    opacity: 100,
  };
}

/** Does the design draw this template at all (directly or through an alias)? */
export function airDesignDraws(templateId: string): boolean {
  return Boolean(AIR_DESIGN.placements[airDesignTemplateId(templateId)]);
}

/** The drawn canvas a template borrows its crop from, or null if it has none. */
export function airAnalogTemplateId(templateId: string): string | null {
  return AIR_TEMPLATE_ANALOGS[airDesignTemplateId(templateId)] ?? null;
}

/** Layouts that move the artwork on at least one template. */
export function airLayoutsWithOwnPlacement(): number[] {
  return Object.keys(AIR_DESIGN.layoutPlacements)
    .map(Number)
    .sort((a, b) => a - b);
}

export interface AirBackgroundTransforms {
  bgX: Record<string, number>;
  bgY: Record<string, number>;
  bgScale: Record<string, number>;
  bgRotate: Record<string, number>;
  bgFlip: Record<string, {x: boolean; y: boolean}>;
}

/**
 * The five per-template transform records a scene and the catalog both store,
 * for one background on one layout.
 *
 * Rotation and flip are written for every template, zeroes included. The
 * default-scene cascade merges these records per template key across layers,
 * and the channel-wide `"*"` rows carry whatever rotation and flip another
 * product's admin last saved — an absent key here would let those through.
 */
export function airBackgroundTransforms(
  templates: {id: string; width: number; height: number}[],
  position: number | string,
  layout: number | string | null = null
): AirBackgroundTransforms {
  const out: AirBackgroundTransforms = {bgX: {}, bgY: {}, bgScale: {}, bgRotate: {}, bgFlip: {}};
  for (const {id, width, height} of templates) {
    const p = airPlacement(id, width, height, position, layout);
    out.bgX[id] = p.x;
    out.bgY[id] = p.y;
    out.bgScale[id] = p.scale;
    out.bgRotate[id] = p.rotate;
    out.bgFlip[id] = {...p.flip};
  }
  return out;
}

export interface AirBackgroundOpacity {
  /** The shared slider value: what every template without its own uses. */
  bgOpacity: number;
  /** Per-template presets, only for templates that differ from the shared value. */
  bgOpacities: Record<string, number>;
}

/**
 * The artwork opacity for one background across a set's templates.
 *
 * The frames fade the artwork per layer — 0.8 on most, 0.7 or 1.0 on some —
 * and a scene has one shared Opacity slider plus per-template presets
 * (`bgOpacities`, see layout/BgTransformer/templateBg.ts). The most common
 * value becomes the slider; the rest become presets, so the chain stays intact
 * and a user moving the slider moves only the templates that follow it.
 */
export function airBackgroundOpacity(
  templates: {id: string; width: number; height: number}[],
  position: number | string,
  layout: number | string | null = null
): AirBackgroundOpacity {
  const values = templates.map(({id, width, height}) => ({
    id,
    opacity: airPlacement(id, width, height, position, layout).opacity,
  }));
  const counts = new Map<number, number>();
  for (const {opacity} of values) counts.set(opacity, (counts.get(opacity) ?? 0) + 1);
  const shared = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0]?.[0] ?? 100;
  const bgOpacities: Record<string, number> = {};
  for (const {id, opacity} of values) {
    if (opacity !== shared) bgOpacities[id] = opacity;
  }
  return {bgOpacity: shared, bgOpacities};
}
